import { FC, useMemo } from "react";
import { Box } from "@mui/material";
import { AxisLabelsFormatterContextObject } from "highcharts";
import { useEffectOnceWhen } from "rooks";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import {
  ResourceExplorer,
  ResourceExplorerWidget,
} from "@/services/cloudchipr.api";
import {
  generateChartData,
  extractChartSettings,
} from "@/components/unauthorized-pages/internal-billing-explorer/utils/helpers/chartHelpers";
import { getHighChartsColumnDataLabels } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsColumnDataLabels";
import { moneyFormatter } from "@/utils/numeral/moneyFormatter";
import { setGlobalVarReadyWithTimeout } from "@/components/unauthorized-pages/common/utils/helpers/setGlobalVarReadyWithTimeout";
import { useInternalQuarterStartMonthHook } from "@/components/unauthorized-pages/common/utils/hooks/useInternalQuarterStartMonth.hook";

interface InternalBillingExplorerChartProps {
  view: ResourceExplorer;
  viewData: ResourceExplorerWidget;
  showBudgetLine?: boolean;
}

export const InternalBillingExplorerChart: FC<
  InternalBillingExplorerChartProps
> = ({ view, viewData, showBudgetLine = false }) => {
  const loading = !viewData;
  const { quarterStartMonth } = useInternalQuarterStartMonthHook();

  const chartData = useMemo(
    () =>
      generateChartData(view, viewData, showBudgetLine, quarterStartMonth ?? 1),
    [view, viewData, showBudgetLine, quarterStartMonth],
  );
  const settings = useMemo(() => extractChartSettings(view), [view]);

  const columnDataLabels = useMemo(
    () =>
      getHighChartsColumnDataLabels(
        settings.chart_type,
        settings.chart_base_type,
      ),
    [settings.chart_type, settings.chart_base_type],
  );

  useEffectOnceWhen(() => {
    setGlobalVarReadyWithTimeout();
  }, !!chartData);

  return (
    <Box height={370} width="100%">
      <MultiTypeHighcharts
        chartData={chartData}
        chartScale={settings.scale}
        chartType={settings.chart_type}
        showLegends={settings.show_legends}
        columnDataLabels={columnDataLabels}
        showDataLabels={settings.show_data_labels}
        loading={loading ? loadingProps : false}
        yAxisLabelsFormatter={yAxisLabelsFormatter}
        cumulativeLine={settings.cumulative_line}
        percentBased={settings.chart_base_type === "percent"}
        tooltipMarkupGenerator={tooltipMarkupGeneratorPlaceholder}
        noDataMessage="No data by your selected date range and filters."
        stackedChartHeadingLabelFormatter={
          settings.chart_base_type === "cost"
            ? formatStackedChartHeadingLabel
            : undefined
        }
      />
    </Box>
  );
};

const tooltipMarkupGeneratorPlaceholder = () => "";

const yAxisLabelsFormatter = (params: AxisLabelsFormatterContextObject) => {
  return `${moneyFormatter(+params.value)}`;
};

const formatStackedChartHeadingLabel = (value: number) => {
  return `${moneyFormatter(value) ?? value}`;
};

const loadingProps = { showLoader: false };
